import { filter, first, map, Observable, ReplaySubject } from 'rxjs';

/** used internally for the id of the queue item if they exceed max number value, they already sus. */
let globalId = 0;

/**
 * Provides a 1-at-a-time request queue for observables.
 * This can break on errors and clear the queue, or continue on errors.
 * Either way each observable is returned to the caller to be subscribed to, once it is processed.
 *
 * Recommend for http queues that you use unknown for response type,
 * as it can vary in most cases where you would want to use this.
 * But for the error, you probably can unify under HttpErrorResponse.
 *
 * Inspired heavily by:
 * @link https://github.com/marcellkiss/rxjs-observable-queue/blob/main/src/observable-queue.ts
 */
export class RequestQueue<ResponseType = unknown, ErrorType = unknown> {
  private queue: QueueItem<ResponseType>[] = [];
  private readonly itemResponse$: ReplaySubject<ItemResponse<ResponseType>> = new ReplaySubject<ItemResponse<ResponseType>>();
  private readonly itemError$: ReplaySubject<ItemError<ErrorType>> = new ReplaySubject<ItemError<ErrorType>>();

  /**
   * Creates an instance of RequestQueue<ResponseType, ErrorType>.
   * @param breakOnError If true, the queue clears and processing stops on first error.
   * Future queued items will process as if a new queue.
   */
  constructor(private readonly breakOnError = false) {}

  /**
   * Adds an observable to the queue to be processed.
   * @param observable The observable to queue - returns a listener for the observable to subscribe to.
   * @returns An observable of the same type to subscribe to. Will be processed after previous queued items are handled.
   */
  public addItem<T>(observable: Observable<T> | (() => Observable<T>)): Observable<T> {
    const id = `${++globalId}`;

    const queueItem: QueueItem<ResponseType> = { id };
    if (typeof observable === 'function') {
      queueItem.factory = observable as unknown as () => Observable<ResponseType>;
    } else {
      queueItem.observable = observable as unknown as Observable<ResponseType>;
    }
    // convert to the response type if needed to handle Observable<unknown> for composite queues
    this.queue.push(queueItem);
    if (this.queue.length === 1) {
      this.processNextItem();
    }

    return this.getObservableById(id) as unknown as Observable<T>; // Type casting to match the generic type T
  }

  private nextItemResponseById(id: string): Observable<ResponseType> {
    return this.itemResponse$.pipe(
      filter((itemResponse: ItemResponse<ResponseType>) => {
        return itemResponse.id === id;
      }),
      map((itemResponse: ItemResponse<ResponseType>) => itemResponse.response)
    );
  }

  private nextErrorResponseById(id: string): Observable<ErrorType> {
    return this.itemError$.pipe(
      filter((itemError: ItemError<ErrorType>) => itemError.id === id),
      map((itemError: ItemError<ErrorType>) => itemError.error)
    );
  }

  private getObservableById(id: string): Observable<ResponseType> {
    return new Observable((subscriber) => {
      this.nextItemResponseById(id)
        .pipe(first())
        .subscribe((res: ResponseType) => {
          subscriber.next(res);
          subscriber.complete();
        });

      this.nextErrorResponseById(id)
        .pipe(first())
        .subscribe((error: ErrorType) => {
          subscriber.error(error);
          subscriber.complete();
        });
    });
  }

  private processNextItem() {
    if (this.queue.at(0)?.factory) {
      // execute the factory before subscription if present
      this.queue.at(0).observable = this.queue.at(0).factory();
    }
    this.queue.at(0)?.observable.subscribe({
      next: (response: ResponseType) => {
        this.itemResponse$.next({ id: this.queue[0].id, response });
      },
      error: (error: ErrorType) => {
        this.itemError$.next({ id: this.queue[0].id, error });
        if (this.breakOnError) {
          this.queue = []; // clear the queue instead of continuing
        } else {
          this.queue.shift();
          this.processNextItem();
        }
      },
      complete: () => {
        this.queue.shift();
        this.processNextItem();
      }
    });
  }
}

interface QueueItem<T> {
  id: string;
  observable?: Observable<T>;
  factory?: () => Observable<T>;
}

interface ItemResponse<T> {
  id: string;
  response: T;
}

interface ItemError<E> {
  id: string;
  error: E;
}
